import { useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';

interface NewBet {
  id: number;
  date: string;
  match: string;
  market: string;
  selection: string;
  odds: number;
  stake: number;
  result: 'won' | 'lost' | 'pending';
  profit: number;
  bookmaker: string;
}

interface RegisterBetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (bet: NewBet) => void;
}

const BOOKMAKERS = ['Bet365', 'Betsson', 'Codere', 'Bwin', '888sport', 'Unibet', 'Betfair', 'Luckia', 'Marca Apuestas', 'Otro'];
const MARKETS = [
  '1X2',
  'Ambos Equipos Marcan',
  'Más/Menos Goles',
  'Más/Menos Corners',
  'Hándicap Asiático',
  'Hándicap Europeo',
  'Resultado Exacto',
  'Primer Goleador',
  'Doble Oportunidad',
  'Ganador del Partido',
  'Total Puntos',
  'Otro',
];

const today = () => {
  const d = new Date();
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }).replace('.', '');
};

export default function RegisterBetModal({ isOpen, onClose, onSave }: RegisterBetModalProps) {
  const { supabaseUser } = useAuth();
  const [match, setMatch] = useState('');
  const [market, setMarket] = useState('');
  const [customMarket, setCustomMarket] = useState('');
  const [selection, setSelection] = useState('');
  const [odds, setOdds] = useState('');
  const [stake, setStake] = useState('');
  const [bookmaker, setBookmaker] = useState('');
  const [result, setResult] = useState<'won' | 'lost' | 'pending'>('pending');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    setMatch('');
    setMarket('');
    setCustomMarket('');
    setSelection('');
    setOdds('');
    setStake('');
    setBookmaker('');
    setResult('pending');
    setErrors({});
    setSaving(false);
    setSuccess(false);
    onClose();
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!match.trim()) e.match = 'Indica el partido.';
    if (!market) e.market = 'Selecciona un mercado.';
    if (market === 'Otro' && !customMarket.trim()) e.customMarket = 'Especifica el mercado.';
    if (!selection.trim()) e.selection = 'Indica tu selección.';
    if (!odds || isNaN(Number(odds)) || Number(odds) < 1.01) e.odds = 'Cuota mínima 1.01.';
    if (!stake || isNaN(Number(stake)) || Number(stake) <= 0) e.stake = 'Introduce un stake válido.';
    if (!bookmaker) e.bookmaker = 'Selecciona una casa.';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    if (!supabaseUser) return;

    setSaving(true);

    const oddsNum = parseFloat(odds);
    const stakeNum = parseFloat(stake);
    const profit =
      result === 'won'
        ? parseFloat(((oddsNum - 1) * stakeNum).toFixed(2))
        : result === 'lost'
        ? -stakeNum
        : 0;

    const finalMarket = market === 'Otro' ? customMarket.trim() : market;

    const { data, error } = await supabase
      .from('bets')
      .insert({
        user_id: supabaseUser.id,
        match_name: match.trim(),
        market: finalMarket,
        selection: selection.trim(),
        odds: oddsNum,
        stake: stakeNum,
        bookmaker,
        result,
        profit,
        bet_date: new Date().toISOString().split('T')[0],
      })
      .select()
      .maybeSingle();

    setSaving(false);

    if (error) {
      setErrors({ match: 'Error al guardar. Inténtalo de nuevo.' });
      return;
    }

    const newBet: NewBet = {
      id: data?.id ?? Date.now(),
      date: new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }),
      match: match.trim(),
      market: finalMarket,
      selection: selection.trim(),
      odds: oddsNum,
      stake: stakeNum,
      result,
      profit,
      bookmaker,
    };

    setSuccess(true);
    onSave(newBet);
    setTimeout(() => {
      handleClose();
    }, 1200);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2d3a] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#00e87a]/15">
              <i className="ri-add-circle-line text-[#00e87a] text-lg"></i>
            </div>
            <h2 className="text-white font-bold text-lg">Registrar Nueva Apuesta</h2>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center text-[#6b7280] hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">

          {/* Partido */}
          <div>
            <label className="block text-[#a8b2c1] text-xs font-medium mb-1.5">Partido <span className="text-[#ef4444]">*</span></label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280]">
                <i className="ri-football-line text-sm"></i>
              </div>
              <input
                type="text"
                value={match}
                onChange={(e) => { setMatch(e.target.value); setErrors((p) => ({ ...p, match: '' })); }}
                placeholder="Ej: Real Madrid vs Barcelona"
                className={`w-full bg-[#0d0f14] border ${errors.match ? 'border-[#ef4444]' : 'border-[#2a2d3a]'} text-white text-sm rounded-xl pl-9 pr-4 py-3 focus:outline-none focus:border-[#00e87a]/60 transition-colors placeholder-[#3d4150]`}
              />
            </div>
            {errors.match && <p className="text-[#ef4444] text-xs mt-1">{errors.match}</p>}
          </div>

          {/* Mercado + Selección */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[#a8b2c1] text-xs font-medium mb-1.5">Mercado <span className="text-[#ef4444]">*</span></label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280] pointer-events-none">
                  <i className="ri-bar-chart-line text-sm"></i>
                </div>
                <select
                  value={market}
                  onChange={(e) => { setMarket(e.target.value); setErrors((p) => ({ ...p, market: '' })); }}
                  className={`w-full bg-[#0d0f14] border ${errors.market ? 'border-[#ef4444]' : 'border-[#2a2d3a]'} text-sm rounded-xl pl-9 pr-4 py-3 focus:outline-none focus:border-[#00e87a]/60 transition-colors cursor-pointer appearance-none ${market ? 'text-white' : 'text-[#3d4150]'}`}
                >
                  <option value="" disabled>Seleccionar</option>
                  {MARKETS.map((m) => (
                    <option key={m} value={m} className="text-white bg-[#151719]">{m}</option>
                  ))}
                </select>
              </div>
              {errors.market && <p className="text-[#ef4444] text-xs mt-1">{errors.market}</p>}
            </div>
            <div>
              <label className="block text-[#a8b2c1] text-xs font-medium mb-1.5">Selección <span className="text-[#ef4444]">*</span></label>
              <input
                type="text"
                value={selection}
                onChange={(e) => { setSelection(e.target.value); setErrors((p) => ({ ...p, selection: '' })); }}
                placeholder="Ej: Real Madrid 1X"
                className={`w-full bg-[#0d0f14] border ${errors.selection ? 'border-[#ef4444]' : 'border-[#2a2d3a]'} text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-[#00e87a]/60 transition-colors placeholder-[#3d4150]`}
              />
              {errors.selection && <p className="text-[#ef4444] text-xs mt-1">{errors.selection}</p>}
            </div>
          </div>

          {/* Mercado personalizado */}
          {market === 'Otro' && (
            <div>
              <label className="block text-[#a8b2c1] text-xs font-medium mb-1.5">Especifica el mercado <span className="text-[#ef4444]">*</span></label>
              <input
                type="text"
                value={customMarket}
                onChange={(e) => { setCustomMarket(e.target.value); setErrors((p) => ({ ...p, customMarket: '' })); }}
                placeholder="Describe el mercado"
                className={`w-full bg-[#0d0f14] border ${errors.customMarket ? 'border-[#ef4444]' : 'border-[#2a2d3a]'} text-white text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-[#00e87a]/60 transition-colors placeholder-[#3d4150]`}
              />
              {errors.customMarket && <p className="text-[#ef4444] text-xs mt-1">{errors.customMarket}</p>}
            </div>
          )}

          {/* Cuota + Stake + Casa */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[#a8b2c1] text-xs font-medium mb-1.5">Cuota <span className="text-[#ef4444]">*</span></label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280] pointer-events-none">
                  <i className="ri-price-tag-3-line text-sm"></i>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="1.01"
                  value={odds}
                  onChange={(e) => { setOdds(e.target.value); setErrors((p) => ({ ...p, odds: '' })); }}
                  placeholder="1.85"
                  className={`w-full bg-[#0d0f14] border ${errors.odds ? 'border-[#ef4444]' : 'border-[#2a2d3a]'} text-white text-sm rounded-xl pl-9 pr-2 py-3 focus:outline-none focus:border-[#00e87a]/60 transition-colors placeholder-[#3d4150]`}
                />
              </div>
              {errors.odds && <p className="text-[#ef4444] text-xs mt-1">{errors.odds}</p>}
            </div>
            <div>
              <label className="block text-[#a8b2c1] text-xs font-medium mb-1.5">Stake (€) <span className="text-[#ef4444]">*</span></label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280] pointer-events-none">
                  <i className="ri-money-euro-circle-line text-sm"></i>
                </div>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={stake}
                  onChange={(e) => { setStake(e.target.value); setErrors((p) => ({ ...p, stake: '' })); }}
                  placeholder="20"
                  className={`w-full bg-[#0d0f14] border ${errors.stake ? 'border-[#ef4444]' : 'border-[#2a2d3a]'} text-white text-sm rounded-xl pl-9 pr-2 py-3 focus:outline-none focus:border-[#00e87a]/60 transition-colors placeholder-[#3d4150]`}
                />
              </div>
              {errors.stake && <p className="text-[#ef4444] text-xs mt-1">{errors.stake}</p>}
            </div>
            <div>
              <label className="block text-[#a8b2c1] text-xs font-medium mb-1.5">Casa <span className="text-[#ef4444]">*</span></label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#6b7280] pointer-events-none">
                  <i className="ri-store-2-line text-sm"></i>
                </div>
                <select
                  value={bookmaker}
                  onChange={(e) => { setBookmaker(e.target.value); setErrors((p) => ({ ...p, bookmaker: '' })); }}
                  className={`w-full bg-[#0d0f14] border ${errors.bookmaker ? 'border-[#ef4444]' : 'border-[#2a2d3a]'} text-sm rounded-xl pl-9 pr-2 py-3 focus:outline-none focus:border-[#00e87a]/60 transition-colors cursor-pointer appearance-none ${bookmaker ? 'text-white' : 'text-[#3d4150]'}`}
                >
                  <option value="" disabled>Casa</option>
                  {BOOKMAKERS.map((b) => (
                    <option key={b} value={b} className="text-white bg-[#151719]">{b}</option>
                  ))}
                </select>
              </div>
              {errors.bookmaker && <p className="text-[#ef4444] text-xs mt-1">{errors.bookmaker}</p>}
            </div>
          </div>

          {/* Resultado */}
          <div>
            <label className="block text-[#a8b2c1] text-xs font-medium mb-2">Resultado</label>
            <div className="flex gap-2">
              {([
                { key: 'won', label: 'Ganada', icon: 'ri-check-double-line', color: '#00e87a' },
                { key: 'lost', label: 'Perdida', icon: 'ri-close-circle-line', color: '#ef4444' },
                { key: 'pending', label: 'Pendiente', icon: 'ri-time-line', color: '#ffd700' },
              ] as const).map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setResult(r.key)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                    result === r.key
                      ? 'border-opacity-60 bg-opacity-15'
                      : 'border-[#2a2d3a] text-[#6b7280] hover:text-white hover:border-white/20'
                  }`}
                  style={
                    result === r.key
                      ? { borderColor: `${r.color}60`, backgroundColor: `${r.color}15`, color: r.color }
                      : {}
                  }
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${r.icon} text-sm`}></i>
                  </div>
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Resumen P/L en tiempo real */}
          {odds && stake && parseFloat(odds) >= 1.01 && parseFloat(stake) > 0 && (
            <div className="bg-[#0d0f14] border border-[#2a2d3a] rounded-xl p-4">
              <p className="text-[#6b7280] text-xs mb-2 font-medium">Resumen estimado</p>
              <div className="flex items-center justify-between">
                <div className="text-center">
                  <p className="text-white text-sm font-black">{parseFloat(odds).toFixed(2)}</p>
                  <p className="text-[#6b7280] text-xs">Cuota</p>
                </div>
                <div className="text-center">
                  <p className="text-white text-sm font-black">{parseFloat(stake).toFixed(2)}€</p>
                  <p className="text-[#6b7280] text-xs">Stake</p>
                </div>
                <div className="text-center">
                  <p className="text-[#00e87a] text-sm font-black">
                    +{((parseFloat(odds) - 1) * parseFloat(stake)).toFixed(2)}€
                  </p>
                  <p className="text-[#6b7280] text-xs">Beneficio si gana</p>
                </div>
                <div className="text-center">
                  <p className="text-white text-sm font-black">
                    {(parseFloat(odds) * parseFloat(stake)).toFixed(2)}€
                  </p>
                  <p className="text-[#6b7280] text-xs">Retorno total</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#2a2d3a] shrink-0">
          {success ? (
            <div className="flex items-center justify-center gap-2 py-3 bg-[#00e87a]/15 rounded-xl border border-[#00e87a]/30">
              <div className="w-4 h-4 flex items-center justify-center text-[#00e87a]">
                <i className="ri-check-double-line text-sm"></i>
              </div>
              <span className="text-[#00e87a] text-sm font-bold">¡Apuesta registrada correctamente!</span>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 py-3 border border-[#2a2d3a] text-[#a8b2c1] text-sm font-medium rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 bg-[#00e87a] text-black text-sm font-bold rounded-xl hover:bg-[#00d070] cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-loader-4-line text-sm animate-spin"></i>
                    </div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-save-line text-sm"></i>
                    </div>
                    Guardar Apuesta
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
